import React from 'react';
import { useTranslation } from 'next-i18next';
import { cn } from '@restworld/utils-common';
import { Button, Logo } from '@restworld/ui-ds-base';

interface InvalidReferralCodeProps {
  onGoBack: () => void;
  onProceedWithout: () => void;
}

export const InvalidReferralCode: React.FC<InvalidReferralCodeProps> = ({ onGoBack, onProceedWithout }) => {
  const { t } = useTranslation('restaurant-onboarding');

  return (
    <Wrapper>
      <Content>
        <Logo variant="horizontal" title="Restworld" className="h-8 w-auto text-purple-700" />
        <Title>{t('invalid_referral_link_title')}</Title>
        <Card>
          <MessageGroup>
            <Message>{t('invalid_referral_link_message')}</Message>
            <Message>{t('invalid_referral_link_try_again')}</Message>
          </MessageGroup>
          <ButtonGroup>
            <Button variant="primary" fullwidth onClick={onProceedWithout}>
              {t('invalid_referral_link_proceed_without')}
            </Button>
            <Button variant="tertiary" fullwidth onClick={onGoBack}>
              {t('invalid_referral_link_ask_another')}
            </Button>
          </ButtonGroup>
        </Card>
      </Content>
    </Wrapper>
  );
};

// Layout Components
const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-stretch min-h-screen', className)} style={{ backgroundColor: '#fafafa' }} {...props} />
);

const Content = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col items-center flex-1 gap-12 px-4 py-10', className)} {...props} />
);

const Title = ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h1
    className={cn('text-xl font-bold text-center text-black', className)}
    style={{ maxWidth: 320, lineHeight: 1.21 }}
    {...props}
  />
);

const Card = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col items-center gap-8 w-full', className)}
    style={{
      maxWidth: 480,
      padding: '40px 20px 20px',
      border: '1px solid #dedee1',
      borderRadius: 16,
      boxShadow: '0px 2px 8px 0px rgba(0, 0, 26, 0.1)',
      backgroundColor: '#fafafa',
    }}
    {...props}
  />
);

const MessageGroup = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col w-full gap-2', className)} {...props} />
);

const Message = ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p
    className={cn('text-base font-medium text-center text-black', className)}
    style={{ lineHeight: 1.21 }}
    {...props}
  />
);

const ButtonGroup = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col items-center w-full gap-4', className)} {...props} />
);
